export type ClientContactRole = 'facturation' | 'administratif' | 'technique';

export type ClientContactLike = {
    externalId?: number | null;
    firstname?: string | null;
    lastname?: string | null;
    email?: string | null;
    tel?: string | null;
    mobile?: string | null;
    roles?: string[] | null;
};

export type ClientWithContacts = {
    email?: string | null;
    company?: string | null;
    firstname?: string | null;
    lastname?: string | null;
    contacts?: ClientContactLike[] | null;
};

export const CLIENT_CONTACT_ROLES: ClientContactRole[] = [
    'facturation',
    'administratif',
    'technique',
];

const ROLE_LABEL_KEYS: Record<ClientContactRole, string> = {
    facturation: 'changes.reply_contact_role_facturation',
    administratif: 'changes.reply_contact_role_administratif',
    technique: 'changes.reply_contact_role_technique',
};

export function normalizeContactRole(role: string | null | undefined): string {
    return String(role || '').trim().toLowerCase();
}

export function contactHasRole(contact: ClientContactLike, role: ClientContactRole): boolean {
    return (contact.roles || []).some((item) => normalizeContactRole(item) === role);
}

export function contactDisplayName(contact: ClientContactLike): string {
    return [contact.firstname, contact.lastname].filter(Boolean).join(' ').trim();
}

export function roleLabelKey(role: ClientContactRole | string): string {
    const normalized = normalizeContactRole(role) as ClientContactRole;
    return ROLE_LABEL_KEYS[normalized] || '';
}

/** Flowbite Badge color for a Zone Client contact role. */
export function roleBadgeColor(role: string | null | undefined): string {
    switch (normalizeContactRole(role)) {
        case 'facturation':
            return 'purple';
        case 'administratif':
            return 'info';
        case 'technique':
            return 'success';
        default:
            return 'gray';
    }
}

/**
 * First contact of the client that has the given role and a usable email.
 */
export function findClientContactByRole(
    client: ClientWithContacts | null | undefined,
    role: ClientContactRole
): ClientContactLike | null {
    const contacts = client?.contacts || [];
    for (const contact of contacts) {
        const email = String(contact?.email || '').trim();
        if (!email)
            continue;
        if (contactHasRole(contact, role))
            return contact;
    }
    return null;
}

/**
 * Shortcut entries for roles that exist on the linked client and are not already in `excludedEmails`.
 */
export function clientContactRoleShortcuts(
    client: ClientWithContacts | null | undefined,
    excludedEmails: string[] = []
): Array<{ role: ClientContactRole; email: string; contact: ClientContactLike }> {
    const excluded = new Set(excludedEmails.map((email) => email.trim().toLowerCase()).filter(Boolean));
    const shortcuts: Array<{ role: ClientContactRole; email: string; contact: ClientContactLike }> = [];

    for (const role of CLIENT_CONTACT_ROLES) {
        const contact = findClientContactByRole(client, role);
        if (!contact)
            continue;
        const email = String(contact.email || '').trim();
        if (!email || excluded.has(email.toLowerCase()))
            continue;
        shortcuts.push({ role, email, contact });
    }

    return shortcuts;
}
